<template>
  <!-- Skip to main content for keyboard users -->
  <a href="#main-content" class="visually-hidden skip-link">Skip to main content</a>

  <div id="main-content" class="admin-container" role="main">
    <header class="admin-header">
      <h1 class="admin-title">PALLAS Admin</h1>
      <!-- Compact Stats HUD -->
      <div class="admin-stats-hud" role="region" aria-label="User Statistics">
        <span class="stat-item"><span class="stat-num">{{ totalUsers }}</span> Users</span>
        <span class="stat-separator">•</span>
        <span class="stat-item"><span class="stat-num">{{ verifiedUsers }}</span> Verified</span>
        <span class="stat-separator">•</span>
        <span class="stat-item"><span class="stat-num">{{ unverifiedUsers }}</span> Pending</span>
      </div>
      <div class="admin-search-controls" role="search">
        <div class="admin-search-section">
          <form action="#" class="admin-search-form">
            <div class="admin-search-input-container">
              <div class="filter-icon-section">
                <button class="filter-button" @click="toggleSortDropdown" type="button"
                        aria-label="Filter and sort options"
                        :aria-expanded="showSortDropdown.toString()"
                        aria-haspopup="true">
                  <svg viewBox="0 0 512 512" height="16" width="16" aria-hidden="true">
                    <path d="M0 416c0 17.7 14.3 32 32 32l54.7 0c12.3 28.3 40.5 48 73.3 48s61-19.7 73.3-48L480 448c17.7 0 32-14.3 32-32s-14.3-32-32-32l-246.7 0c-12.3-28.3-40.5-48-73.3-48s-61 19.7-73.3 48L32 384c-17.7 0-32 14.3-32 32zm128 0a32 32 0 1 1 64 0 32 32 0 1 1 -64 0zM320 256a32 32 0 1 1 64 0 32 32 0 1 1 -64 0zm32-80c-32.8 0-61 19.7-73.3 48L32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l246.7 0c12.3 28.3 40.5 48 73.3 48s61-19.7 73.3-48l54.7 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-54.7 0c-12.3-28.3-40.5-48-73.3-48zM192 128a32 32 0 1 1 0-64 32 32 0 1 1 0 64zm73.3-64C253 35.7 224.8 16 192 16s-61 19.7-73.3 48L32 64C14.3 64 0 78.3 0 96s14.3 32 32 32l86.7 0c12.3 28.3 40.5 48 73.3 48s61-19.7 73.3-48L480 128c17.7 0 32-14.3 32-32s-14.3-32-32-32L265.3 64z" fill="currentColor"/>
                  </svg>
                </button>
                
                <div v-if="showSortDropdown" class="admin-filter-dropdown">
                  <div class="filter-section">
                    <div class="filter-header">Filter by Status</div>
                    <div class="filter-option" @click.stop="toggleFilter('verified')">
                      <input
                        type="checkbox"
                        :checked="activeFilters.verified"
                        class="filter-checkbox"
                        tabindex="-1"
                      />
                      <span class="checkbox-label">Verified</span>
                    </div>
                    <div class="filter-option" @click.stop="toggleFilter('unverified')">
                      <input
                        type="checkbox"
                        :checked="activeFilters.unverified"
                        class="filter-checkbox"
                        tabindex="-1"
                      />
                      <span class="checkbox-label">Unverified</span>
                    </div>
                  </div>
                </div>
              </div>
              <label for="admin-search" class="visually-hidden">Search users by name, email, or date</label>
              <input type="text" id="admin-search" class="admin-search-input" placeholder="Search PALLAS Admin..." v-model="searchQuery" autocomplete="off" />
              <button class="search-clear-btn" type="button" @click="clearSearch"
                      aria-label="Clear search" v-if="searchQuery">
                ✕
              </button>
            </div>
          </form>
        </div>
      </div>
    </header>

    <section class="users-section" role="region" aria-labelledby="users-title">
      <h2 id="users-title" class="visually-hidden">User Management</h2>
      <div v-if="isLoading" class="loading-state" role="status" aria-live="polite">
        <div class="loading-spinner" aria-hidden="true"></div>
        <p>Loading users...</p>
      </div>

      <div v-else-if="errorMessage" class="error-state" role="alert">
        <div class="error-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        </div>
        <h3>Error Loading Users</h3>
        <p>{{ errorMessage }}</p>
        <button @click="loadUsers" class="retry-btn" aria-label="Retry loading users">Try Again</button>
      </div>

      <div v-else-if="filteredUsers.length === 0" class="empty-state" role="status" aria-live="polite">
        <div class="empty-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
        </div>
        <h3>No Users Found</h3>
        <p>{{ searchQuery ? 'Try adjusting your search criteria.' : 'No users match the current filter.' }}</p>
      </div>

      <div v-else class="users-table-container">
        <!-- Table Header -->
        <div class="table-header">
          <div class="th-avatar"></div>
          <div class="th-name sortable" @click="setSortOption('alpha-asc')" :class="{ active: sortOption === 'alpha-asc' || sortOption === 'alpha-desc' }">
            Name
            <span class="sort-icon">{{ sortOption === 'alpha-desc' ? '↓' : '↑' }}</span>
          </div>
          <div class="th-email">Email</div>
          <div class="th-date sortable" @click="setSortOption('date-desc')" :class="{ active: sortOption === 'date-desc' || sortOption === 'date-asc' }">
            Joined
            <span class="sort-icon">{{ sortOption === 'date-asc' ? '↑' : '↓' }}</span>
          </div>
          <div class="th-status">Status</div>
          <div class="th-permissions">Permissions</div>
        </div>

        <!-- Table Rows -->
        <div class="table-body">
          <div v-for="user in filteredUsers" :key="user.uid || user.id" class="table-row">
            <div class="td-avatar">
              <img
                v-if="user.profilePicture"
                :src="user.profilePicture"
                :alt="user.name || 'User'"
                class="row-avatar"
                @error="handleImageError"
              />
              <div v-else class="row-avatar-placeholder">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              </div>
            </div>
            <div class="td-name">{{ user.name || 'No Name' }}</div>
            <div class="td-email">{{ user.email }}</div>
            <div class="td-date">{{ formatDate(user.created) }}</div>
            <div class="td-status">
              <button
                @click="toggleVerification(user)"
                :class="['status-badge', 'clickable', user.verified ? 'verified' : 'pending']"
                :title="user.verified ? 'Click to set as Pending' : 'Click to Verify'">
                <span class="badge-check">{{ user.verified ? '✓' : '○' }}</span>
                {{ user.verified ? 'Verified' : 'Pending' }}
              </button>
            </div>
            <div class="td-permissions">
              <button
                @click="toggleAdminStatus(user)"
                :class="['permission-badge', 'clickable', (user.adminRole === 'admin' || user.role === 'admin') ? 'admin' : 'base']"
                :title="(user.adminRole === 'admin' || user.role === 'admin') ? 'Click to set as Base user' : 'Click to grant Admin'">
                <span class="badge-check">{{ (user.adminRole === 'admin' || user.role === 'admin') ? '✓' : '○' }}</span>
                {{ (user.adminRole === 'admin' || user.role === 'admin') ? 'Admin' : 'Base' }}
              </button>
            </div>
          </div>
        </div>

        <div class="table-footer">
          <span>{{ filteredUsers.length }} {{ filteredUsers.length === 1 ? 'user' : 'users' }}</span>
        </div>
      </div>
    </section>

    <!-- ===== TAG MANAGEMENT SECTION ===== -->
    <section class="tags-section" role="region" aria-labelledby="tags-title">
      <h2 id="tags-title" class="section-title">Tag Management</h2>

      <!-- Tag Controls Row: Search + Buttons -->
      <div class="tags-controls-row">
        <!-- Tag Search with Filter Dropdown -->
        <div class="tag-search-input-container">
          <div class="tag-filter-icon-section">
            <button class="tag-filter-button" @click="toggleTagFilterDropdown" type="button"
                    aria-label="Filter tag options"
                    :aria-expanded="showTagFilterDropdown.toString()"
                    aria-haspopup="true">
              <svg viewBox="0 0 512 512" height="16" width="16" aria-hidden="true">
                <path d="M0 416c0 17.7 14.3 32 32 32l54.7 0c12.3 28.3 40.5 48 73.3 48s61-19.7 73.3-48L480 448c17.7 0 32-14.3 32-32s-14.3-32-32-32l-246.7 0c-12.3-28.3-40.5-48-73.3-48s-61 19.7-73.3 48L32 384c-17.7 0-32 14.3-32 32zm128 0a32 32 0 1 1 64 0 32 32 0 1 1 -64 0zM320 256a32 32 0 1 1 64 0 32 32 0 1 1 -64 0zm32-80c-32.8 0-61 19.7-73.3 48L32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l246.7 0c12.3 28.3 40.5 48 73.3 48s61-19.7 73.3-48l54.7 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-54.7 0c-12.3-28.3-40.5-48-73.3-48zM192 128a32 32 0 1 1 0-64 32 32 0 1 1 0 64zm73.3-64C253 35.7 224.8 16 192 16s-61 19.7-73.3 48L32 64C14.3 64 0 78.3 0 96s14.3 32 32 32l86.7 0c12.3 28.3 40.5 48 73.3 48s61-19.7 73.3-48L480 128c17.7 0 32-14.3 32-32s-14.3-32-32-32L265.3 64z" fill="currentColor"/>
              </svg>
            </button>

            <div v-if="showTagFilterDropdown" class="tag-filter-dropdown">
              <div class="filter-section">
                <div class="filter-header">Filter by Type</div>
                <div class="filter-option" @click.stop="setTagFilter('all'); showTagFilterDropdown = false" :class="{ active: tagFilter === 'all' }">
                  <span class="filter-option-text">All Tags</span>
                  <span v-if="tagFilter === 'all'" class="filter-check">✓</span>
                </div>
                <div class="filter-option" @click.stop="setTagFilter('art'); showTagFilterDropdown = false" :class="{ active: tagFilter === 'art' }">
                  <span class="filter-option-text filter-art">Art</span>
                  <span v-if="tagFilter === 'art'" class="filter-check">✓</span>
                </div>
                <div class="filter-option" @click.stop="setTagFilter('science'); showTagFilterDropdown = false" :class="{ active: tagFilter === 'science' }">
                  <span class="filter-option-text filter-science">Science</span>
                  <span v-if="tagFilter === 'science'" class="filter-check">✓</span>
                </div>
              </div>
            </div>
          </div>
          <label for="tag-search" class="visually-hidden">Search tags</label>
          <input type="text" id="tag-search" class="tag-search-input" placeholder="Search tags..." v-model="tagSearchQuery" autocomplete="off" />
          <button class="search-clear-btn" type="button" @click="clearTagSearch"
                  aria-label="Clear search" v-if="tagSearchQuery">
            ✕
          </button>
        </div>

        <!-- Tag Action Button -->
        <button class="btn-tag-action" @click="openAddTagModal">
          + Add Tag
        </button>
      </div>

      <!-- Success/Error Messages -->
      <div v-if="tagSuccess" class="tag-message tag-success" role="status">
        {{ tagSuccess }}
      </div>
      <div v-if="tagError" class="tag-message tag-error" role="alert">
        {{ tagError }}
      </div>

      <!-- Tags Loading State -->
      <div v-if="tagsLoading" class="loading-state" role="status" aria-live="polite">
        <div class="loading-spinner" aria-hidden="true"></div>
        <p>Loading tags...</p>
      </div>

      <!-- Tags Grid -->
      <div v-else-if="filteredTags.length === 0" class="empty-state" role="status">
        <p>{{ tagSearchQuery ? 'No tags match your search.' : 'No tags available.' }}</p>
      </div>

      <div v-else class="tags-grid">
        <button
          v-for="tag in filteredTags"
          :key="tag.name"
          class="tag-pill"
          :data-theme="tag.theme"
          @click="openDeleteTagModal(tag)"
          :aria-label="`Delete tag ${tag.name}`"
        >
          <span class="tag-action">−</span>
          <span class="tag-text">{{ tag.name }}</span>
        </button>
      </div>

      <div class="tags-footer">
        <span>{{ filteredTags.length }} {{ filteredTags.length === 1 ? 'tag' : 'tags' }} shown</span>
      </div>
    </section>

    <!-- Add Tag Modal -->
    <div v-if="showAddTagModal" class="modal-overlay" @click="closeAddTagModal" role="dialog" aria-modal="true" aria-labelledby="add-tag-modal-title">
      <div class="modal-content" @click.stop>
        <h2 id="add-tag-modal-title" class="modal-title">Add New Tag</h2>

        <div class="form-group">
          <label for="new-tag-name" class="form-label">Tag Name</label>
          <input
            type="text"
            id="new-tag-name"
            class="form-input"
            v-model="newTagName"
            placeholder="Enter tag name..."
            @keyup.enter="handleAddTag"
          />
        </div>

        <div class="form-group">
          <label class="form-label">Theme</label>
          <div class="theme-toggle">
            <button
              :class="['theme-option', 'theme-art', { active: newTagTheme === 'art' }]"
              @click="newTagTheme = 'art'"
              type="button"
            >
              Art
            </button>
            <button
              :class="['theme-option', 'theme-science', { active: newTagTheme === 'science' }]"
              @click="newTagTheme = 'science'"
              type="button"
            >
              Science
            </button>
          </div>
        </div>

        <div v-if="tagError" class="modal-error">
          {{ tagError }}
        </div>

        <div class="modal-actions">
          <button @click="closeAddTagModal" class="btn-secondary">Cancel</button>
          <button @click="handleAddTag" class="btn-primary">Add Tag</button>
        </div>
      </div>
    </div>

    <!-- Delete Tag Confirmation Modal -->
    <div v-if="showDeleteTagModal" class="modal-overlay" @click="closeDeleteTagModal" role="dialog" aria-modal="true" aria-labelledby="delete-tag-modal-title">
      <div class="modal-content" @click.stop>
        <h2 id="delete-tag-modal-title" class="modal-title">Delete Tag</h2>
        <p class="modal-text">
          Are you sure you want to delete the tag
          <strong :class="['tag-highlight', `tag-${tagToDelete?.theme}`]">{{ tagToDelete?.name }}</strong>?
        </p>
        <p class="modal-warning">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
          </svg>
          This action cannot be undone. Users with this tag may see it removed from their profiles.
        </p>
        <div class="modal-actions">
          <button @click="closeDeleteTagModal" class="btn-secondary">Cancel</button>
          <button @click="confirmDeleteTag" class="btn-primary">Delete</button>
        </div>
      </div>
    </div>

    <div v-if="showAdminConfirmModal" class="modal-overlay" @click="cancelAdminToggle" role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <div class="modal-content" @click.stop>
        <h2 id="modal-title" class="modal-title">Confirm Admin Status Change</h2>
        <p class="modal-text">
          Are you sure you want to
          <strong>{{ (userToToggleAdmin?.adminRole === 'admin' || userToToggleAdmin?.role === 'admin') ? 'remove admin status from' : 'grant admin status to' }}</strong>
          {{ userToToggleAdmin?.name || 'this user' }}?
        </p>
        <p class="modal-warning" v-if="userToToggleAdmin?.adminRole !== 'admin' && userToToggleAdmin?.role !== 'admin'">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
          </svg>
          Admin users have full access to manage all users and platform settings.
        </p>
        <div class="modal-actions">
          <button @click="cancelAdminToggle" class="btn-secondary">Cancel</button>
          <button @click="confirmAdminToggle" class="btn-primary" :class="{ 'btn-danger': userToToggleAdmin?.adminRole === 'admin' || userToToggleAdmin?.role === 'admin' }">
            {{ (userToToggleAdmin?.adminRole === 'admin' || userToToggleAdmin?.role === 'admin') ? 'Remove' : 'Grant Admin' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { getFirestore, collection, getDocs, doc, updateDoc } from 'firebase/firestore'
import { getAuth } from 'firebase/auth'
import { useTagsAdmin } from '@/composables/useTags.js'

export default {
  name: 'AdminDashboard',
  setup() {
    const users = ref([]);
    const isLoading = ref(true);
    const errorMessage = ref('');
    const searchQuery = ref('');
    const sortOption = ref('date-desc');
    const showSortDropdown = ref(false);
    const searchInput = ref(null);
    const activeFilters = ref({
      verified: false,
      unverified: false
    });
    const db = getFirestore();
    const auth = getAuth();

    const showAdminConfirmModal = ref(false);
    const userToToggleAdmin = ref(null);
    const currentUser = ref(null);

    // === TAG MANAGEMENT STATE ===
    const {
      tags,
      artTags,
      scienceTags,
      tagCount,
      isLoading: tagsLoading,
      isLoaded: tagsLoaded,
      addTag: addTagToStore,
      removeTag: removeTagFromStore
    } = useTagsAdmin();

    const tagSearchQuery = ref('');
    const tagFilter = ref('all'); // 'all', 'art', 'science'
    const showTagFilterDropdown = ref(false);
    const showAddTagModal = ref(false);
    const showDeleteTagModal = ref(false);
    const tagToDelete = ref(null);
    const newTagName = ref('');
    const newTagTheme = ref('art');
    const tagError = ref('');
    const tagSuccess = ref('');

    const sortOptions = [
      { value: 'date-desc', label: 'Newest First' },
      { value: 'date-asc', label: 'Oldest First' },
      { value: 'alpha-asc', label: 'A-Z' },
      { value: 'alpha-desc', label: 'Z-A' }
    ];

    const totalUsers = computed(() => users.value.length);
    const verifiedUsers = computed(() => users.value.filter(u => u.verified).length);
    const unverifiedUsers = computed(() => users.value.filter(u => !u.verified).length);
    const recentUsers = computed(() => {
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      return users.value.filter(u => u.created && new Date(u.created) > oneWeekAgo).length;
    });

    const filteredUsers = computed(() => {
      let filtered = [...users.value];

      if (searchQuery.value.trim()) {
        const query = searchQuery.value.toLowerCase();
        filtered = filtered.filter(user => {
          const nameMatch = (user.name || '').toLowerCase().includes(query);
          const emailMatch = (user.email || '').toLowerCase().includes(query);
          const dateMatch = user.created && formatDate(user.created).toLowerCase().includes(query);
          return nameMatch || emailMatch || dateMatch;
        });
      }

      const hasActiveFilters = Object.values(activeFilters.value).some(v => v);

      if (hasActiveFilters) {
        filtered = filtered.filter(user => {
          if (activeFilters.value.verified && user.verified) return true;
          if (activeFilters.value.unverified && !user.verified) return true;
          return false;
        });
      }

      return sortUsers(filtered);
    });

    // === TAG MANAGEMENT COMPUTED ===
    const filteredTags = computed(() => {
      let result = [...tags.value];

      // Filter by theme
      if (tagFilter.value === 'art') {
        result = result.filter(tag => tag.theme === 'art');
      } else if (tagFilter.value === 'science') {
        result = result.filter(tag => tag.theme === 'science');
      }

      // Filter by search query
      if (tagSearchQuery.value.trim()) {
        const query = tagSearchQuery.value.toLowerCase();
        result = result.filter(tag =>
          tag.name.toLowerCase().includes(query)
        );
      }

      // Sort alphabetically by name
      result.sort((a, b) => a.name.localeCompare(b.name));

      return result;
    });

    const sortUsers = (userList) => {
      return userList.sort((a, b) => {
        switch (sortOption.value) {
          case 'date-asc':
            return new Date(a.created || 0) - new Date(b.created || 0);
          case 'alpha-asc':
            return (a.name || '').localeCompare(b.name || '');
          case 'alpha-desc':
            return (b.name || '').localeCompare(a.name || '');
          case 'date-desc':
          default:
            return new Date(b.created || 0) - new Date(a.created || 0);
        }
      });
    };

    const loadUsers = async () => {
      try {
        isLoading.value = true;
        errorMessage.value = '';

        const snapshot = await getDocs(collection(db, 'users'));
        const userList = [];

        snapshot.forEach(docSnap => {
          try {
            const data = docSnap.data() || {};
          let createdDate = null;

            if (data.createdAt && data.createdAt.toDate) {
              createdDate = data.createdAt.toDate();
            } 

          let profilePicture = null;
            if (data.profile?.profilePictureURL) {
              profilePicture = data.profile.profilePictureURL;
            } 

            userList.push({
              uid: docSnap.id,
              email: data.email || '',
              name: data.profile?.name || `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'No Name',
              verified: !!data.verified,
              role: data.role || 'artist',
              adminRole: data.adminRole || null,
              created: createdDate,
              profilePicture: profilePicture,
              profile: data.profile || {}
            });
          } catch (docError) {
            console.error('Error processing user document:', docSnap.id, docError);
          }
        });

        users.value = userList;
      } catch (error) {
        console.error('Error fetching users:', error);
        errorMessage.value = 'Failed to load user data. Please try again.';
      } finally {
        isLoading.value = false;
      }
    };

    const toggleVerification = async (user) => {
      try {
        const userRef = doc(db, 'users', user.uid);
        const newVerificationStatus = !user.verified;
        await updateDoc(userRef, { verified: newVerificationStatus });
        user.verified = newVerificationStatus;
      } catch (error) {
        console.error('Error updating user verification:', error);
        errorMessage.value = 'Failed to update verification status.';
      }
    };

    const clearSearch = () => {
      searchQuery.value = '';
    };

    const toggleAdminStatus = (user) => {
      userToToggleAdmin.value = user;
      showAdminConfirmModal.value = true;
    };

    const confirmAdminToggle = async () => {
      if (!userToToggleAdmin.value) return;

      try {
        const user = userToToggleAdmin.value;
        const userRef = doc(db, 'users', user.uid);
        // Use new dual role system
        const isCurrentlyAdmin = user.adminRole === 'admin' || user.role === 'admin';
        
        if (isCurrentlyAdmin) {
          // Remove admin privileges
          await updateDoc(userRef, { 
            adminRole: null,
            // Keep legacy role for backward compatibility if it exists
            ...(user.role === 'admin' && { role: user.profileRole || 'artist' })
          });
          user.adminRole = null;
          if (user.role === 'admin') {
            user.role = user.profileRole || 'artist';
          }
        } else {
          // Grant admin privileges
          await updateDoc(userRef, { adminRole: 'admin' });
          user.adminRole = 'admin';
        }
      } catch (error) {
        console.error('Error updating user role:', error);
        errorMessage.value = 'Failed to update user role.';
      } finally {
        showAdminConfirmModal.value = false;
        userToToggleAdmin.value = null;
      }
    };

    const cancelAdminToggle = () => {
      showAdminConfirmModal.value = false;
      userToToggleAdmin.value = null;
    };

    const toggleSortDropdown = () => {
      showSortDropdown.value = !showSortDropdown.value;
    };

    const selectSortOption = (value) => {
      sortOption.value = value;
      showSortDropdown.value = false;
    };

    const setSortOption = (baseOption) => {
      // Toggle between asc/desc for the same column
      if (baseOption === 'alpha-asc') {
        sortOption.value = sortOption.value === 'alpha-asc' ? 'alpha-desc' : 'alpha-asc';
      } else if (baseOption === 'date-desc') {
        sortOption.value = sortOption.value === 'date-desc' ? 'date-asc' : 'date-desc';
      }
    };

    const toggleFilter = (filterName) => {
      activeFilters.value[filterName] = !activeFilters.value[filterName];
    };

    const handleClickOutside = (event) => {
      const isDropdownButton = event.target.closest('.filter-button');
      if (isDropdownButton) {
        return;
      }

      const isDropdownContent = event.target.closest('.admin-filter-dropdown');
      if (isDropdownContent) {
        return;
      }

      showSortDropdown.value = false;
    };

    const formatDate = (timestamp) => {
      if (!timestamp) return 'No Date';
      const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    };

    const handleImageError = (event) => {
      event.target.style.display = 'none';
      const placeholder = event.target.nextElementSibling;
      if (placeholder && placeholder.classList.contains('avatar-placeholder')) {
         placeholder.style.display = 'flex';
      }
    };

    // === TAG MANAGEMENT FUNCTIONS ===
    const getAdminUid = () => {
      return auth.currentUser?.uid || 'unknown';
    };

    const toggleTagFilterDropdown = () => {
      showTagFilterDropdown.value = !showTagFilterDropdown.value;
    };

    const openAddTagModal = () => {
      newTagName.value = '';
      newTagTheme.value = 'art';
      tagError.value = '';
      showAddTagModal.value = true;
    };

    const closeAddTagModal = () => {
      showAddTagModal.value = false;
      newTagName.value = '';
      newTagTheme.value = 'art';
      tagError.value = '';
    };

    const handleAddTag = async () => {
      tagError.value = '';
      tagSuccess.value = '';

      if (!newTagName.value.trim()) {
        tagError.value = 'Tag name is required';
        return;
      }

      try {
        await addTagToStore(newTagName.value.trim(), newTagTheme.value, getAdminUid());
        tagSuccess.value = `Tag "${newTagName.value.trim()}" added successfully`;
        closeAddTagModal();

        // Clear success message after 3 seconds
        setTimeout(() => {
          tagSuccess.value = '';
        }, 3000);
      } catch (error) {
        tagError.value = error.message;
      }
    };

    const openDeleteTagModal = (tag) => {
      tagToDelete.value = tag;
      showDeleteTagModal.value = true;
    };

    const closeDeleteTagModal = () => {
      showDeleteTagModal.value = false;
      tagToDelete.value = null;
    };

    const confirmDeleteTag = async () => {
      if (!tagToDelete.value) return;

      tagError.value = '';
      tagSuccess.value = '';

      try {
        await removeTagFromStore(tagToDelete.value.name, getAdminUid());
        tagSuccess.value = `Tag "${tagToDelete.value.name}" deleted successfully`;
        closeDeleteTagModal();

        // Clear success message after 3 seconds
        setTimeout(() => {
          tagSuccess.value = '';
        }, 3000);
      } catch (error) {
        tagError.value = error.message;
        closeDeleteTagModal();
      }
    };

    const clearTagSearch = () => {
      tagSearchQuery.value = '';
    };

    const setTagFilter = (filter) => {
      tagFilter.value = filter;
    };

    onMounted(() => {
      loadUsers();
      document.addEventListener('click', handleClickOutside);
      const userInfo = localStorage.getItem('user-info');
      if (userInfo) {
        currentUser.value = JSON.parse(userInfo);
      }
    });

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside);
    });

    return {
      users,
      isLoading,
      errorMessage,
      searchQuery,
      sortOption,
      showSortDropdown,
      searchInput,
      activeFilters,
      sortOptions,
      totalUsers,
      verifiedUsers,
      unverifiedUsers,
      recentUsers,
      filteredUsers,
      loadUsers,
      toggleVerification,
      clearSearch,
      toggleSortDropdown,
      selectSortOption,
      setSortOption,
      toggleFilter,
      formatDate,
      handleImageError,
      currentUser,
      showAdminConfirmModal,
      userToToggleAdmin,
      toggleAdminStatus,
      confirmAdminToggle,
      cancelAdminToggle,
      // Tag management
      tags,
      artTags,
      scienceTags,
      tagCount,
      tagsLoading,
      tagsLoaded,
      filteredTags,
      tagSearchQuery,
      tagFilter,
      showTagFilterDropdown,
      toggleTagFilterDropdown,
      showAddTagModal,
      showDeleteTagModal,
      tagToDelete,
      newTagName,
      newTagTheme,
      tagError,
      tagSuccess,
      openAddTagModal,
      closeAddTagModal,
      handleAddTag,
      openDeleteTagModal,
      closeDeleteTagModal,
      confirmDeleteTag,
      clearTagSearch,
      setTagFilter
    };
  }
}
</script>

<style scoped>
/* ===== ACCESSIBILITY STYLES ===== */
.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

.skip-link {
  position: absolute;
  top: -40px;
  left: 6px;
  background: #6566be;
  color: white;
  padding: 8px;
  text-decoration: none;
  border-radius: 4px;
  z-index: 10000;
  font-weight: 600;
  transition: top 0.2s ease;
}

.skip-link:focus {
  top: 6px;
}

/* Enhanced focus indicators for keyboard users */
.filter-button:focus:not(:focus-visible),
.action-btn:focus:not(:focus-visible),
.search-clear-btn:focus:not(:focus-visible),
.retry-btn:focus:not(:focus-visible),
.btn-secondary:focus:not(:focus-visible),
.btn-primary:focus:not(:focus-visible) {
  outline: none;
}

.filter-button:focus-visible,
.action-btn:focus-visible,
.search-clear-btn:focus-visible,
.retry-btn:focus-visible,
.btn-secondary:focus-visible,
.btn-primary:focus-visible,
.admin-search-input:focus-visible {
  outline: 2px solid #6566be;
  outline-offset: 2px;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .admin-user-card,
  .admin-stat-card,
  .modal-content {
    border-width: 3px;
  }

  .loading-spinner {
    border-width: 4px;
  }

  .action-btn {
    border-width: 3px;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .loading-spinner {
    animation: none;
  }

  .admin-user-card:hover,
  .admin-stat-card:hover,
  .action-btn:hover,
  .retry-btn:hover,
  .btn-secondary:hover,
  .btn-primary:hover {
    transform: none;
    transition: none;
  }

  .skip-link {
    transition: none;
  }
}

/* Touch target size for mobile */
@media (max-width: 768px) {
  .filter-button,
  .search-clear-btn,
  .action-btn,
  .retry-btn,
  .btn-secondary,
  .btn-primary {
    min-height: 44px;
    min-width: 44px;
  }
}

/* ===== MAIN ADMIN CONTAINER ===== */
.admin-container {
  width: 100%;
  min-height: 100vh;
  padding-top: var(--navbarHeight);
}

/* ===== ADMIN HEADER ===== */
.admin-header {
  position: relative;
  width: 100%;
  padding: var(--paddingSpaceous) 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--paddingSpaceous);
}

.admin-title {
  font-size: 3.5rem;
  font-weight: var(--font-weight-bold);
  font-family: var(--font-primary);
  color: var(--headersColor);
  text-align: center;
  margin: 0 0 var(--paddingTight) 0;
  z-index: 10;
  position: relative;
  letter-spacing: -0.02em;
}

/* ===== SEARCH AND FILTER CONTROLS ===== */
.admin-search-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 600px;
  z-index: 10;
  position: relative;
  margin-bottom: var(--paddingSpaceous);
}

.admin-search-section {
  width: 100%;
  max-width: none;
}

.admin-search-form {
  width: 100%;
  position: relative;
}

.admin-search-input-container {
  position: relative;
  background: white;
  border-radius: 10px;
  border: 1px solid var(--borderColor);
  display: flex;
  align-items: center;
  width: 100%;
  transition: box-shadow var(--stdTransition);
}

.admin-search-input-container:focus-within {
  box-shadow: 0 0 0 2px var(--sciColor);
}

.filter-icon-section {
  padding: var(--paddingTight);
  border-right: 1px solid var(--borderColor);
  display: flex;
  align-items: center;
  flex-shrink: 0;
  position: relative;
}

.filter-button {
  background: transparent;
  border: none;
  padding: var(--paddingTight);
  cursor: pointer;
  color: var(--textColor);
  transition: all var(--stdTransition);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.filter-button:hover {
  background: var(--neutralColor);
  color: var(--sciColor);
}

.admin-search-input {
  flex: 1;
  padding: var(--paddingTight) var(--paddingSpaceous);
  border: none;
  border-radius: 0;
  font-size: var(--buttonText);
  font-family: var(--font-primary);
  color: var(--textColor);
  background: transparent;
  outline: none;
  transition: all var(--stdTransition);
}

.admin-search-input::placeholder {
  color: var(--textColor);
  opacity: 0.6;
}

.search-clear-btn {
  padding: var(--paddingTight);
  margin: 0 var(--paddingTight) 0 0;
  background: transparent;
  border: none;
  font-size: var(--labelText);
  color: var(--textColor);
  cursor: pointer;
  opacity: 0.6;
  transition: all var(--stdTransition);
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  flex-shrink: 0;
}

.search-clear-btn:hover {
  opacity: 1;
  background: var(--neutralColor);
}

/* ===== ADMIN FILTER DROPDOWN ===== */
.admin-filter-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 8px;
  background: white;
  border-radius: var(--stdRadius);
  box-shadow: var(--stdShadow);
  border: 1px solid var(--borderColor);
  z-index: 1000;
  min-width: 200px;
  max-width: 280px;
  font-family: var(--font-primary);
}



.filter-section {
  padding: var(--paddingTight) 0;
  border-bottom: 1px solid var(--borderColor);
}

.filter-section:last-child {
  border-bottom: none;
}

.filter-header {
  padding: 0 var(--paddingStandard) var(--paddingTight);
  font-size: var(--labelText);
  font-weight: var(--font-weight-bold);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--textColor);
  opacity: 0.7;
}

.filter-option {
  display: flex;
  align-items: center;
  gap: var(--paddingTight);
  padding: var(--paddingTight) var(--paddingStandard);
  cursor: pointer;
  color: var(--textColor);
  font-size: var(--buttonText);
  font-weight: var(--font-weight-medium);
  transition: background-color var(--stdTransition);
}

.filter-option:hover {
  background: var(--neutralColor);
  color: var(--headersColor);
}

.filter-checkbox {
  width: 16px;
  height: 16px;
  border: 2px solid var(--borderColor);
  border-radius: 3px;
  background: white;
  cursor: pointer;
  position: relative;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  transition: all var(--stdTransition);
  flex-shrink: 0;
}

.filter-checkbox:checked {
  background: var(--sciColor);
  border-color: var(--sciColor);
}

.filter-checkbox:checked::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 4px;
  height: 8px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: translate(-50%, -60%) rotate(45deg);
}

.checkbox-label {
  user-select: none;
}

.sort-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--paddingTight) var(--paddingStandard);
  cursor: pointer;
  color: var(--textColor);
  font-size: var(--buttonText);
  font-weight: var(--font-weight-medium);
  transition: background-color var(--stdTransition);
  border-bottom: 1px solid var(--borderColor);
  position: relative;
}

.sort-option:first-child {
  border-radius: var(--stdRadius) var(--stdRadius) 0 0;
}

.sort-option:last-child {
  border-bottom: none;
  border-radius: 0 0 var(--stdRadius) var(--stdRadius);
}

.sort-option:hover {
  background: var(--neutralColor);
  color: var(--headersColor);
}

.sort-option.active {
  background: var(--artOutlineColor);
  color: var(--artColor);
  font-weight: var(--font-weight-bold);
}

.sort-option.active::after {
  content: '✓';
  color: var(--artColor);
  font-weight: var(--font-weight-bold);
  font-size: 16px;
}

/* ===== COMPACT STATS HUD ===== */
.admin-stats-hud {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px 12px;
  font-size: 1rem;
  font-style: italic;
  background: linear-gradient(90deg, var(--artColor) 0%, var(--sciColor) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  margin-bottom: var(--paddingStandard);
}

.stat-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.stat-num {
  font-weight: 600;
}

.stat-separator {
  font-size: 0.7rem;
}

.users-section {
  background: rgba(255, 255, 255, 0.95);
  border-radius: var(--stdRadius);
  padding: var(--paddingSpaceous);
  border: 1px solid rgba(255, 255, 255, 0.2);
  max-width: 1000px;
  margin: 0 auto;
}

.loading-state, .error-state, .empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: calc(var(--paddingSpaceous) * 2) var(--paddingSpaceous);
  text-align: center;
  color: var(--textColor);
  font-family: var(--font-primary);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--borderColor);
  border-top: 3px solid var(--artColor);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: var(--paddingStandard);
  will-change: transform;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .loading-spinner {
    animation: none;
  }
}

.error-icon, .empty-icon {
  width: 48px;
  height: 48px;
  color: var(--textColor);
  opacity: 0.6;
  margin-bottom: var(--paddingStandard);
}

.error-state h3, .empty-state h3 {
  margin: 0 0 var(--paddingTight) 0;
  color: var(--headersColor);
  font-family: var(--font-primary);
  font-weight: var(--font-weight-bold);
}

.retry-btn {
  margin-top: var(--paddingStandard);
  padding: var(--paddingTight) var(--paddingSpaceous);
  background: var(--artColor);
  color: white;
  border: none;
  border-radius: var(--stdRadius);
  font-weight: var(--font-weight-bold);
  font-family: var(--font-primary);
  cursor: pointer;
  transition: all var(--stdTransition);
}

.retry-btn:hover {
  background: var(--sciColor);
  transform: var(--hoverLift);
}

/* ===== TABLE LAYOUT ===== */
.users-table-container {
  width: 100%;
}

.table-header {
  display: grid;
  grid-template-columns: 40px 1fr 1.5fr 100px 90px 100px;
  gap: var(--paddingTight);
  padding: var(--paddingTight) var(--paddingStandard);
  background: var(--neutralColor);
  border-radius: var(--stdRadius) var(--stdRadius) 0 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--textColor);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.table-header > div {
  display: flex;
  align-items: center;
  gap: 4px;
}

.table-header .sortable {
  cursor: pointer;
  user-select: none;
  transition: color var(--stdTransition);
}

.table-header .sortable:hover {
  color: var(--sciColor);
}

.table-header .sortable.active {
  color: var(--sciColor);
}

.sort-icon {
  font-size: 0.65rem;
  opacity: 0.6;
}

.sortable.active .sort-icon {
  opacity: 1;
}

.table-body {
  border: 1px solid var(--borderColor);
  border-top: none;
  border-radius: 0 0 var(--stdRadius) var(--stdRadius);
  overflow: hidden;
}

.table-row {
  display: grid;
  grid-template-columns: 40px 1fr 1.5fr 100px 90px 100px;
  gap: var(--paddingTight);
  padding: var(--paddingTight) var(--paddingStandard);
  background: white;
  border-bottom: 1px solid var(--borderColor);
  align-items: center;
  font-size: 0.8rem;
  transition: background var(--stdTransition);
}

.table-row:last-child {
  border-bottom: none;
}

.table-row:hover {
  background: var(--neutralColor);
}

.td-avatar {
  display: flex;
  justify-content: center;
}

.row-avatar, .row-avatar-placeholder {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}

.row-avatar-placeholder {
  background: var(--neutralColor);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--textColor);
  opacity: 0.5;
}

.row-avatar-placeholder svg {
  width: 16px;
  height: 16px;
}

.td-name {
  font-weight: 600;
  color: var(--headersColor);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.td-email {
  color: var(--textColor);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.td-date {
  color: var(--textColor);
  opacity: 0.7;
  font-size: 0.75rem;
}

.td-status,
.td-permissions {
  display: flex;
  justify-content: center;
}

/* Shared clickable badge styles */
.status-badge,
.permission-badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.status-badge.clickable,
.permission-badge.clickable {
  cursor: pointer;
  transition: all var(--stdTransition);
}

.badge-check {
  font-size: 0.7rem;
  line-height: 1;
}

/* Status badge - Verified (active) */
.status-badge.verified {
  background: #d1d2e6;
  color: var(--headersColor);
}

.status-badge.verified:hover {
  background: var(--headersColor);
  color: white;
}

/* Status badge - Pending (inactive) */
.status-badge.pending {
  background: #e8e8e8;
  color: #666;
}

.status-badge.pending:hover {
  background: #d0d0d0;
  color: #444;
}

/* Permission badge - Admin (active) */
.permission-badge.admin {
  background: #d1d2e6;
  color: var(--headersColor);
}

.permission-badge.admin:hover {
  background: var(--headersColor);
  color: white;
}

/* Permission badge - Base (inactive) */
.permission-badge.base {
  background: #e8e8e8;
  color: #666;
}

.permission-badge.base:hover {
  background: #d0d0d0;
  color: #444;
}

.table-footer {
  padding: var(--paddingTight) var(--paddingStandard);
  text-align: right;
  font-size: 0.75rem;
  color: var(--textColor);
  opacity: 0.6;
}

/* ===== RESPONSIVE DESIGN ===== */
@media (max-width: var(--breakpointTablet)) {
  .admin-container {
    padding-top: calc(var(--navbarHeight) * 0.8);
  }

  .admin-header {
    padding: var(--paddingStandard) var(--paddingStandard) var(--paddingSpaceous) var(--paddingStandard);
  }

  .admin-title {
    font-size: calc(var(--title) * 0.9);
  }

  .admin-search-controls {
    width: 100%;
    padding: 0 var(--paddingStandard);
  }

  .users-section {
    padding: var(--paddingStandard);
    margin: 0 var(--paddingTight);
  }

  .table-header, .table-row {
    grid-template-columns: 32px 1fr 80px 90px;
  }

  .th-email, .td-email, .th-date, .td-date {
    display: none;
  }
}

@media (max-width: 600px) {
  .admin-search-controls {
    padding: 0 var(--paddingTight);
  }

  .admin-title {
    font-size: calc(var(--title) * 0.8);
  }

  .users-section {
    padding: var(--paddingStandard) var(--paddingTight);
    margin: 0 var(--paddingTight);
  }

  .table-header, .table-row {
    grid-template-columns: 32px 1fr 70px 80px;
  }

  .th-email, .td-email, .th-date, .td-date {
    display: none;
  }

  .status-badge,
  .permission-badge {
    padding: 3px 6px;
    font-size: 0.55rem;
  }

  .badge-check {
    font-size: 0.6rem;
  }
}

/* ===== MODAL STYLES ===== */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: var(--bckColor);
  border-radius: var(--stdRadius);
  padding: var(--paddingSpaceous);
  max-width: 450px;
  width: 90%;
  box-shadow: var(--stdShadow);
  border: 2px solid var(--borderColor);
}

.modal-title {
  font-size: var(--title);
  font-weight: var(--font-weight-bold);
  color: var(--headersColor);
  font-family: var(--font-primary);
  margin-bottom: var(--paddingStandard);
}

.modal-text {
  font-size: var(--buttonText);
  color: var(--textColor);
  font-family: var(--font-primary);
  margin-bottom: var(--paddingStandard);
  line-height: 1.5;
}

.modal-warning {
  display: flex;
  align-items: center;
  gap: var(--paddingTight);
  background: var(--neutralColor);
  border: 1px solid var(--borderColor);
  padding: var(--paddingTight) var(--paddingStandard);
  border-radius: var(--stdRadius);
  color: var(--textColor);
  font-size: var(--labelText);
  font-family: var(--font-primary);
  margin-bottom: var(--paddingSpaceous);
}

.modal-warning svg {
  flex-shrink: 0;
  color: var(--textColor);
}

.modal-actions {
  display: flex;
  gap: var(--paddingTight);
  justify-content: flex-end;
}

.btn-secondary,
.btn-primary {
  padding: var(--paddingTight) var(--paddingSpaceous);
  border-radius: var(--stdRadius);
  font-weight: var(--font-weight-bold);
  font-size: var(--buttonText);
  font-family: var(--font-primary);
  transition: all var(--stdTransition);
  border: 2px solid transparent;
  cursor: pointer;
}

.btn-secondary {
  background: #e8e8e8;
  color: #666;
  border-color: #d0d0d0;
}

.btn-secondary:hover {
  background: #d0d0d0;
  color: #444;
  transform: var(--hoverLift);
}

.btn-primary {
  background: #d1d2e6;
  color: var(--headersColor);
  border-color: #d1d2e6;
}

.btn-primary:hover {
  background: #b8b9d4;
  border-color: #b8b9d4;
  transform: var(--hoverLift);
}

/* Danger button - grey themed for consistency */
.btn-danger {
  background: #e8e8e8;
  color: #666;
  border-color: #d0d0d0;
}

.btn-danger:hover {
  background: #d0d0d0;
  color: #444;
  border-color: #b8b8b8;
}

/* ===== TAG MANAGEMENT STYLES ===== */
.tags-section {
  background: rgba(255, 255, 255, 0.95);
  border-radius: var(--stdRadius);
  padding: var(--paddingSpaceous);
  border: 1px solid rgba(255, 255, 255, 0.2);
  max-width: 1000px;
  margin: var(--paddingSpaceous) auto 0;
}

.section-title {
  font-size: 1.5rem;
  font-weight: var(--font-weight-bold);
  color: var(--headersColor);
  margin: 0 0 var(--paddingStandard) 0;
}

/* Tag Controls Row - search left, buttons right */
.tags-controls-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--paddingStandard);
  width: 100%;
  margin-bottom: var(--paddingStandard);
}

.tag-search-input-container {
  position: relative;
  background: white;
  border-radius: 10px;
  border: 1px solid var(--borderColor);
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 320px;
  transition: box-shadow var(--stdTransition);
}

.tag-search-input-container:focus-within {
  border-color: #999;
}

.tag-filter-icon-section {
  padding: var(--paddingTight);
  border-right: 1px solid var(--borderColor);
  display: flex;
  align-items: center;
  flex-shrink: 0;
  position: relative;
}

.tag-filter-button {
  background: transparent;
  border: none;
  padding: var(--paddingTight);
  cursor: pointer;
  color: var(--textColor);
  transition: all var(--stdTransition);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tag-filter-button:hover {
  background: var(--neutralColor);
  color: var(--sciColor);
}

.tag-filter-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 8px;
  background: white;
  border-radius: var(--stdRadius);
  box-shadow: var(--stdShadow);
  border: 1px solid var(--borderColor);
  z-index: 1000;
  min-width: 160px;
  font-family: var(--font-primary);
}

.tag-filter-dropdown .filter-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--paddingTight) var(--paddingStandard);
  cursor: pointer;
  transition: background-color var(--stdTransition);
}

.tag-filter-dropdown .filter-option:hover {
  background: var(--neutralColor);
}

.tag-filter-dropdown .filter-option.active {
  background: var(--neutralColor);
}

.filter-option-text {
  font-size: var(--buttonText);
  color: var(--textColor);
}

.filter-option-text.filter-art {
  color: var(--artColor);
}

.filter-option-text.filter-science {
  color: var(--sciColor);
}

.filter-check {
  color: var(--sciColor);
  font-weight: bold;
}

.tag-search-input {
  flex: 1;
  padding: var(--paddingTight) var(--paddingStandard);
  border: none;
  border-radius: 0;
  font-size: var(--buttonText);
  font-family: var(--font-primary);
  background: white;
  transition: box-shadow var(--stdTransition);
}

.tag-search-input:focus {
  outline: none;
  box-shadow: 0 0 0 2px var(--sciColor);
}

.tag-search-container .search-clear-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
}

/* Add Tag Button */
.btn-tag-action {
  padding: 0.4rem 0.8rem;
  background: white;
  border: 1px solid #C1C1C1;
  border-radius: calc(var(--stdRadius) / 2);
  font-weight: 600;
  font-size: var(--labelText);
  font-family: var(--font-primary);
  cursor: pointer;
  transition: all 0.2s ease;
  color: #333;
  white-space: nowrap;
}

.btn-tag-action:hover {
  background: #f5f5f5;
  border-color: #999;
}

/* Tags Grid */
.tags-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: var(--paddingStandard) 0;
}

/* Tag Pills - matching gallery card compact style */
.tag-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: var(--neutralColor);
  border: var(--stdOutline) solid var(--borderColor);
  border-radius: 4px;
  font-size: 10px;
  font-family: var(--font-primary);
  color: var(--textColor);
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.tag-pill[data-theme="art"] {
  background: var(--bckColor);
  border: var(--stdOutline) solid var(--artOutlineColor);
  color: var(--textColor);
}

.tag-pill[data-theme="art"] .tag-action {
  color: var(--artColor);
}

.tag-pill[data-theme="art"] .tag-text {
  color: #555;
}

.tag-pill[data-theme="science"] {
  background: var(--bckColor);
  border: var(--stdOutline) solid var(--sciOutlineColor);
  color: var(--textColor);
}

.tag-pill[data-theme="science"] .tag-action {
  color: var(--sciColor);
}

.tag-pill[data-theme="science"] .tag-text {
  color: #555;
}

.tag-pill[data-theme="art"]:hover {
  background: var(--artOutlineColor);
  border-color: var(--artColor);
}

.tag-pill[data-theme="art"]:hover .tag-action {
  color: var(--artColor);
}

.tag-pill[data-theme="science"]:hover {
  background: var(--sciOutlineColor);
  border-color: var(--sciColor);
}

.tag-pill[data-theme="science"]:hover .tag-action {
  color: var(--sciColor);
}

.tag-action {
  font-weight: bold;
  font-size: 12px;
  color: var(--textColor);
  line-height: 1;
  min-width: 12px;
  text-align: center;
}

.tag-text {
  font-size: 10px;
  color: #555;
}

.tags-footer {
  padding: var(--paddingTight) 0;
  text-align: right;
  font-size: 0.75rem;
  color: var(--textColor);
  opacity: 0.6;
}

/* Modal Form Styles */
.form-group {
  margin-bottom: var(--paddingStandard);
}

.form-label {
  display: block;
  margin-bottom: 6px;
  font-size: var(--buttonText);
  font-weight: var(--font-weight-medium);
  color: var(--textColor);
}

.form-input {
  width: 100%;
  padding: var(--paddingTight) var(--paddingStandard);
  border: 1px solid var(--borderColor);
  border-radius: var(--stdRadius);
  font-size: var(--buttonText);
  font-family: var(--font-primary);
  background: white;
  transition: box-shadow var(--stdTransition);
}

.form-input:focus {
  outline: none;
  box-shadow: 0 0 0 2px var(--sciColor);
}

/* Theme Toggle */
.theme-toggle {
  display: flex;
  gap: 8px;
}

.theme-option {
  flex: 1;
  padding: var(--paddingTight) var(--paddingStandard);
  border: 2px solid var(--borderColor);
  border-radius: var(--stdRadius);
  background: white;
  font-size: var(--buttonText);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-primary);
  cursor: pointer;
  transition: all var(--stdTransition);
  color: var(--textColor);
}

.theme-option:hover {
  border-color: #ccc;
}

.theme-option.theme-art.active {
  background: rgba(248, 177, 155, 0.2);
  border-color: #f8b19b;
  color: #d4846a;
}

.theme-option.theme-science.active {
  background: rgba(101, 102, 190, 0.15);
  border-color: #6566be;
  color: #5456a8;
}

.modal-error {
  padding: var(--paddingTight) var(--paddingStandard);
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  border-radius: var(--stdRadius);
  margin-bottom: var(--paddingStandard);
  font-size: var(--labelText);
}

.tag-highlight {
  padding: 2px 8px;
  border-radius: 4px;
}

.tag-highlight.tag-art {
  background: rgba(248, 177, 155, 0.2);
  color: #d4846a;
}

.tag-highlight.tag-science {
  background: rgba(101, 102, 190, 0.15);
  color: #5456a8;
}

/* Responsive adjustments for tags section */
@media (max-width: 768px) {
  .tags-section {
    padding: var(--paddingStandard);
    margin: var(--paddingStandard) var(--paddingTight) 0;
  }

  .tags-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .tag-search-container {
    max-width: none;
  }

  .tag-filter-tabs {
    justify-content: center;
  }

  .btn-add-tag,
  .btn-seed-tags {
    width: 100%;
    text-align: center;
  }
}
</style>